import { useMemo } from 'react';
import { DropdownSearch, Dropdown, GroupedExpandableList } from 'component-library';
import { useTranslation } from 'react-i18next';
import {
  type ArticleDeclaration,
  type ArticleGroup,
  type ArticleGroupChild,
  type ListDropdownItem,
} from '@sr/types';

export const MaterialPicker = ({
  onChange,
  materialType,
  articles,
  isPending,
  errorMessage,
}: {
  onChange: (type: string, name: string) => void;
  materialType: string | undefined;
  articles: ArticleGroup[];
  isPending: boolean;
  errorMessage?: string;
}) => {
  const { t } = useTranslation();

  const articleGroups = useMemo(() => {
    if (isPending) {
      return [];
    }

    const articleGroups: ListDropdownItem[] = articles.map((article: ArticleGroup) => ({
      label: article.groupName,
      type: 'group' as const,
      value: article.groupName,
      children: Array.isArray(article.children)
        ? article.children.map((child: ArticleGroupChild) => ({
            label: child.articleName,
            description: child.articleCode,
            meta: {
              description: child.articleCode,
            },
            type: 'value' as const,
            value: child.articleCode,
            children: Array.isArray(child.children)
              ? child.children.map((declaration: ArticleDeclaration) => ({
                  label: declaration.declarationName,
                  description: `${child.articleCode}, ${declaration.declarationCode}`,
                  meta: {
                    description: declaration.declarationCode,
                  },
                  type: 'value' as const,
                  value: declaration.declarationKey,
                  children: [],
                }))
              : [],
          }))
        : [],
    }));

    articleGroups.push({
      label: t('rented-containers.add-containers.step1.add-container.modal.material.article.other'),
      type: 'group',
      value: 'other',
      children: [],
    });

    const filteredArticleGroup = articleGroups.filter(
      (group) => group.label !== 'Confidential services',
    );

    return filteredArticleGroup;
  }, [articles, isPending, t]);

  const handleMaterialNameChange = (value: string) => {
    const materialName = findChildInArticleGroups(articleGroups, value)?.label || '';

    return materialName;
  };

  return (
    <Dropdown
      loading={isPending}
      error={!!errorMessage}
      errorMessage={errorMessage}
      items={articleGroups}
      selectedValues={materialType ? [materialType] : []}
      onChange={(value) => {
        onChange(value[0], handleMaterialNameChange(value[0]));
      }}
      label={t('rented-containers.add-containers.step1.add-container.modal.material.label')}
      placeholder={t(
        'rented-containers.add-containers.step1.add-container.modal.material.placeholder',
      )}
      searchPlaceholder={t(
        'rented-containers.add-containers.step1.add-container.modal.material.search.placeholder',
      )}
      descriptionOnValue={true}
    >
      <DropdownSearch />
      <GroupedExpandableList />
    </Dropdown>
  );
};

const findChildInArticleGroups = (
  articleGroups: ListDropdownItem[],
  value: string,
): ListDropdownItem | undefined => {
  for (const group of articleGroups) {
    for (const child of group.children) {
      if (child.value === value) {
        return child;
      }

      if (child.children.length > 0) {
        const foundInDeclarations = child.children.find(
          (declaration) => declaration.value === value,
        );

        if (foundInDeclarations) {
          return foundInDeclarations;
        }
      }
    }
  }

  return undefined;
};
